import { create } from "zustand";

export type UploadStatus = "queued" | "running" | "done" | "skipped" | "error";

export interface UploadTask {
  taskId: string;
  fileName: string;
  totalBytes: number;
  bytesDone: number;
  status: UploadStatus;
  error?: string;
  // Blob-URL preview shown in the Transfers panel. Set by runImport
  // once the thumbnail is generated; revoked by the store when the
  // task leaves the queue so we don't leak object URLs.
  thumbnailUrl?: string;
}

interface UploadQueueState {
  tasks: Record<string, UploadTask>;
  enqueue: (task: UploadTask) => void;
  update: (taskId: string, patch: Partial<UploadTask>) => void;
  remove: (taskId: string) => void;
  clearDone: () => void;
}

function revokeTaskThumbnail(task: UploadTask | undefined): void {
  if (task?.thumbnailUrl) URL.revokeObjectURL(task.thumbnailUrl);
}

export const useUploadQueue = create<UploadQueueState>((set) => ({
  tasks: {},
  enqueue: (task) =>
    set((state) => ({ tasks: { ...state.tasks, [task.taskId]: task } })),
  update: (taskId, patch) =>
    set((state) => {
      const existing = state.tasks[taskId];
      if (!existing) return state;
      // If the patch is replacing a thumbnail URL with a new one, the
      // old blob URL has to be revoked or it leaks.
      if (
        patch.thumbnailUrl !== undefined &&
        existing.thumbnailUrl &&
        existing.thumbnailUrl !== patch.thumbnailUrl
      ) {
        URL.revokeObjectURL(existing.thumbnailUrl);
      }
      return {
        tasks: { ...state.tasks, [taskId]: { ...existing, ...patch } },
      };
    }),
  remove: (taskId) =>
    set((state) => {
      const existing = state.tasks[taskId];
      revokeTaskThumbnail(existing);
      const rest = { ...state.tasks };
      delete rest[taskId];
      return { tasks: rest };
    }),
  clearDone: () =>
    set((state) => {
      const kept: Record<string, UploadTask> = {};
      for (const [id, t] of Object.entries(state.tasks)) {
        const terminal =
          t.status === "done" ||
          t.status === "error" ||
          t.status === "skipped";
        if (terminal) {
          revokeTaskThumbnail(t);
        } else {
          kept[id] = t;
        }
      }
      return { tasks: kept };
    }),
}));
